// Utility functions for transcription components

// Function to calculate word count
export const calculateWordCount = (text) => {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(word => word.length > 0).length;
};

// Function to parse duration to seconds
export const parseDurationToSeconds = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return 0;
    return value > 100000 ? Math.floor(value / 1000) : Math.floor(value);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return 0;
    if (/^\d+(\.\d+)?$/.test(trimmed)) {
      const num = parseFloat(trimmed);
      return num > 100000 ? Math.floor(num / 1000) : Math.floor(num);
    }
    if (/^\d{1,2}(:\d{2}){1,2}$/.test(trimmed)) {
      const parts = trimmed.split(':').map(p => parseInt(p, 10));
      let seconds = 0;
      if (parts.length === 3) seconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
      else if (parts.length === 2) seconds = parts[0] * 60 + parts[1];
      return seconds;
    }
    const isoMatch = trimmed.match(/^P(T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)$/i);
    if (isoMatch) {
      const hours = parseInt(isoMatch[2] || '0', 10);
      const minutes = parseInt(isoMatch[3] || '0', 10);
      const secs = parseFloat(isoMatch[4] || '0');
      return Math.floor(hours * 3600 + minutes * 60 + secs);
    }
  }
  return 0;
};

// Function to format duration from seconds to hours and minutes
export const formatDuration = (seconds) => {
  // Handle various invalid cases
  if (!seconds || seconds === null || seconds === undefined || isNaN(seconds) || seconds === 'NaN') {
    return 'Unknown';
  }
  
  // Convert to number if it's a string
  const numSeconds = Number(seconds);
  if (isNaN(numSeconds) || numSeconds < 0) {
    return 'Unknown';
  }
  
  const hours = Math.floor(numSeconds / 3600);
  const minutes = Math.floor((numSeconds % 3600) / 60);
  const remainingSeconds = Math.floor(numSeconds % 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return `${minutes}m`;
  } else {
    return `${remainingSeconds}s`;
  }
};

// Function to format date
export const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
};

// Function to clean and normalize markdown content
export const cleanMarkdownContent = (content) => {
  if (!content || typeof content !== 'string') return content;

  let cleaned = content;

  // Remove percentage signs first
  cleaned = cleaned.replace(/%/g, '');

  // Handle the specific pattern: "TEXT## NUMBER. TEXT" (like "COURT PROCEEDING SUMMARY - U.S. SUPREME COURT## 1. CASE HEADER INFORMATION")
  cleaned = cleaned.replace(/(.*?)(#{2,})\s*(\d+\.\s*.*?)(?=\n|$)/g, (match, before, hashes, after) => {
    return before + '\n' + after;
  });

  // Handle hash tags at the beginning of lines like "## HEADER"
  cleaned = cleaned.replace(/^#{1,6}\s*(.*?)(?=\n|$)/gm, '\n$1\n');
  
  // Handle any remaining hash tags anywhere in the text (more aggressive)
  cleaned = cleaned.replace(/#{1,6}\s*([^#\n]+)/g, (match, headerText) => {
    // Extract just the header text without hash tags
    return '\n' + headerText.trim() + '\n';
  });
  
  // Final cleanup - remove any remaining hash tags
  cleaned = cleaned.replace(/#{1,6}/g, '');
  
  // Extra aggressive cleanup for the specific pattern we're seeing
  cleaned = cleaned.replace(/COURT PROCEEDING SUMMARY - U\.S\. SUPREME COURT## 1\. CASE HEADER INFORMATION/g, 'COURT PROCEEDING SUMMARY - U.S. SUPREME COURT\n1. CASE HEADER INFORMATION');

  // Remove multiple asterisks and hashes at the beginning of lines
  cleaned = cleaned.replace(/^\s*\*{3,}\s*/gm, '');
  cleaned = cleaned.replace(/^\s*#{3,}\s*/gm, '');
  
  // Fix malformed headers like "*** ### **TITLE**" -> "### TITLE"
  cleaned = cleaned.replace(/^\s*\*{2,}\s*#{1,6}\s*\*{2,}\s*(.*?)\*{2,}\s*$/gm, (match, title) => {
    const hashCount = (match.match(/#/g) || []).length;
    return '#'.repeat(hashCount) + ' ' + title.trim();
  });
  
  // Fix headers with mixed syntax like "--- ### **TITLE**" -> "### TITLE"
  cleaned = cleaned.replace(/^\s*-{3,}\s*#{1,6}\s*\*{2,}\s*(.*?)\*{2,}\s*$/gm, (match, title) => {
    const hashCount = (match.match(/#/g) || []).length;
    return '#'.repeat(hashCount) + ' ' + title.trim();
  });
  
  // Fix headers with just asterisks like "**TITLE**" -> "## TITLE"
  cleaned = cleaned.replace(/^\s*\*{2,}\s*([^*]+)\*{2,}\s*$/gm, '## $1');
  
  // Clean up standalone asterisks, hashes, and dashes
  cleaned = cleaned.replace(/^\s*[\*#-]+\s*$/gm, '');
  
  // Fix bold text that's not properly formatted (multiple asterisks)
  cleaned = cleaned.replace(/\*{3,}([^*]+)\*{3,}/g, '**$1**');
  cleaned = cleaned.replace(/\*{2,}([^*]+)\*{2,}/g, '**$1**');
  
  // Fix italic text that's not properly formatted
  cleaned = cleaned.replace(/\*{1}([^*]+)\*{1}/g, '*$1*');
  
  // Clean up multiple consecutive newlines
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
  
  // Clean up extra spaces around headers
  cleaned = cleaned.replace(/^\s*#{1,6}\s+/gm, (match) => match.trim() + ' ');
  
  // Trim whitespace
  cleaned = cleaned.trim();

  return cleaned;
};

const _narrativeFrom = (summaryData) => {
  if (!summaryData || typeof summaryData !== 'object') return '';
  const value =
    summaryData.content ||
    summaryData.text ||
    summaryData.summary ||
    summaryData.legal_summary ||
    summaryData.summary_text ||
    summaryData.ai_summary ||
    '';
  return typeof value === 'string' ? value.trim() : '';
};

const _structuredFrom = (summaryData) => {
  const nested =
    summaryData && typeof summaryData === 'object' && summaryData.summary_structured
      ? summaryData.summary_structured
      : summaryData;
  if (!nested || typeof nested !== 'object') {
    return { parties_present: [], key_rulings: [], next_hearing_date: null };
  }
  return {
    parties_present: Array.isArray(nested.parties_present)
      ? nested.parties_present.map((item) => String(item).trim()).filter(Boolean)
      : [],
    key_rulings: Array.isArray(nested.key_rulings)
      ? nested.key_rulings.map((item) => String(item).trim()).filter(Boolean)
      : [],
    next_hearing_date:
      nested.next_hearing_date != null && String(nested.next_hearing_date).trim()
        ? String(nested.next_hearing_date).trim()
        : null,
  };
};

/** Merge API narrative + summary_structured into one display payload. */
export const mergeSummaryPayload = (data) => {
  if (!data) return null;

  if (typeof data === 'string') {
    const text = data.trim();
    if (!text) return null;
    if (text.startsWith('{') && text.includes('parties_present')) {
      try {
        return mergeSummaryPayload(JSON.parse(text));
      } catch {
        return { summary: text, parties_present: [], key_rulings: [], next_hearing_date: null };
      }
    }
    return { summary: text, parties_present: [], key_rulings: [], next_hearing_date: null };
  }

  if (typeof data !== 'object') return null;

  const structured = _structuredFrom(data);
  const narrative = _narrativeFrom(data);
  const merged = {
    summary: narrative,
    parties_present: structured.parties_present,
    key_rulings: structured.key_rulings,
    next_hearing_date: structured.next_hearing_date,
  };
  return isUsableSummary(merged) ? merged : null;
};

/** Pick the best summary from a transcript API payload (all languages). */
export const extractSummaryFromRecord = (data) => {
  if (!data || typeof data !== 'object') return null;

  const structured = _structuredFrom(data);
  const candidates = [
    data.summary,
    data.transcription_summary,
    data.legal_summary,
    data.summary_text,
    data.ai_summary,
    data.content_summary,
    data.metadata?.summary,
    data.analysis?.summary,
    data.results?.summary,
    Array.isArray(data.summaries) ? data.summaries[0] : null,
  ];

  for (const candidate of candidates) {
    const merged = mergeSummaryPayload(
      typeof candidate === 'object' && candidate
        ? { ...candidate, summary_structured: candidate.summary_structured || structured }
        : { summary: candidate, summary_structured: structured }
    );
    if (merged) return merged;
  }

  return mergeSummaryPayload({ summary: '', summary_structured: structured });
};

// True when the value is a usable narrative summary, not an empty structured shell.
export const isUsableSummary = (summaryData) => {
  if (!summaryData) return false;
  if (typeof summaryData === 'string') {
    const text = summaryData.trim();
    if (!text) return false;
    if (text.startsWith('{') && text.includes('parties_present')) {
      try {
        return isUsableSummary(JSON.parse(text));
      } catch {
        return true;
      }
    }
    return true;
  }
  if (typeof summaryData === 'object') {
    if (_narrativeFrom(summaryData)) return true;
    const structured = _structuredFrom(summaryData);
    return (
      structured.parties_present.length > 0 ||
      structured.key_rulings.length > 0 ||
      Boolean(structured.next_hearing_date)
    );
  }
  return false;
};

const COURT_SUMMARY_SECTIONS = [
  'CASE / MATTER IDENTIFICATION',
  'PARTICIPANTS',
  'PURPOSE',
  'SUMMARY',
  'KEY POINTS',
  'DECISIONS AND ORDERS',
  'ACTION ITEMS',
  'NEXT DATE',
  'OPEN QUESTIONS',
  'RECORDING QUALITY',
  'TRANSCRIPT QUALITY',
  'BRIEF VERSION',
];
const COURT_PARTICIPANT_GROUPS = [
  'PRESIDING OFFICER',
  'COUNSEL AND LEGAL REPRESENTATIVES',
  'PARTIES',
  'WITNESSES, DEPONENTS, AND OTHERS NAMED OR PRESENT',
];

const stripHeadingMarks = (line) => line.replace(/^[#*_\s]+|[*_:\s]+$/g, '').trim();

// True when the summary is the headed CourtScribe document from the summary agent.
export const isCourtSummaryDocument = (text) => {
  if (typeof text !== 'string') return false;
  const found = new Set(
    text
      .split('\n')
      .map((line) => stripHeadingMarks(line).toUpperCase())
      .filter((key) => COURT_SUMMARY_SECTIONS.includes(key))
  );
  return found.has('DECISIONS AND ORDERS') || found.size >= 3;
};

const formatCourtSummaryDocument = (text) => {
  const blocks = [];
  let bullets = [];
  const flush = () => {
    if (bullets.length) {
      blocks.push(bullets.join('\n'));
      bullets = [];
    }
  };
  text.split('\n').forEach((raw) => {
    const line = raw.trim();
    if (!line || /^[-_=*]{3,}$/.test(line)) {
      flush();
      return;
    }
    const key = stripHeadingMarks(line).toUpperCase();
    if (COURT_SUMMARY_SECTIONS.includes(key)) {
      flush();
      blocks.push(`### ${key}`);
      return;
    }
    if (COURT_PARTICIPANT_GROUPS.includes(key)) {
      flush();
      blocks.push(`**${stripHeadingMarks(line)}**`);
      return;
    }
    const bullet = line.match(/^[-*•]\s+(.*)$/);
    if (bullet) {
      bullets.push(`- ${bullet[1]}`);
      return;
    }
    flush();
    blocks.push(line);
  });
  flush();
  return blocks.join('\n\n');
};

// Function to format summary for display (narrative + structured sections)
export const formatSummary = (summaryData) => {
  const merged = mergeSummaryPayload(summaryData);
  if (!merged) {
    return null;
  }

  if (merged.summary && isCourtSummaryDocument(merged.summary)) {
    return formatCourtSummaryDocument(merged.summary);
  }

  const lines = [];
  const hasStructured =
    merged.parties_present.length > 0 ||
    merged.key_rulings.length > 0 ||
    Boolean(merged.next_hearing_date);

  if (merged.summary) {
    const narrative = cleanMarkdownContent(merged.summary);
    if (hasStructured) {
      lines.push(`**Hearing summary**\n\n${narrative}`);
    } else {
      const sentences = narrative
        .split(/(?<=[.!?])\s+/)
        .map((part) => part.trim())
        .filter(Boolean);
      if (sentences.length >= 2) {
        lines.push(
          `**Hearing summary**\n${sentences.map((sentence) => `- ${sentence}`).join('\n')}`
        );
      } else {
        lines.push(narrative);
      }
    }
  }
  if (merged.parties_present.length) {
    lines.push(
      `**Parties present**\n${merged.parties_present.map((p) => `- ${p}`).join('\n')}`
    );
  }
  if (merged.key_rulings.length) {
    lines.push(`**Key rulings**\n${merged.key_rulings.map((r) => `- ${r}`).join('\n')}`);
  }
  if (merged.next_hearing_date) {
    lines.push(`**Next hearing**\n${merged.next_hearing_date}`);
  }
  return lines.length ? lines.join('\n\n') : null;
};

// Function to format transcription content for display
export const formatTranscription = (transcriptionData) => {
  if (!transcriptionData) {
    return null;
  }

  // If transcription is a string, clean it and return
  if (typeof transcriptionData === 'string') {
    return cleanMarkdownContent(transcriptionData);
  }

  // If transcription is an object, try to extract meaningful content
  if (typeof transcriptionData === 'object') {
    // Try different possible keys for transcription content
    const content = transcriptionData.content || transcriptionData.text ||
      transcriptionData.transcription || transcriptionData.transcribed_content;
    if (content) {
      return cleanMarkdownContent(content);
    }

    return JSON.stringify(transcriptionData, null, 2);
  }

  return String(transcriptionData);
};

// Function to strip HTML tags from QuillJS content for display
export const stripHtmlTags = (html) => {
  if (!html) return '';
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

// Function to detect if version comes from Snwolley API or local version control
export const isSnwolleyVersion = (version) => {
  if (!version) return false;
  
  // Check if version has Snwolley API indicators
  // Snwolley versions typically have HTML content or specific metadata
  const hasHtmlContent = version.content && version.content.includes('<') && version.content.includes('>');
  const hasSnwolleyMetadata = version.version_metadata && version.version_metadata.source === 'snwolley';
  
  
  // If content is HTML or has Snwolley metadata, it's from Snwolley API
  return hasHtmlContent || hasSnwolleyMetadata;
};

// Function to convert local version markdown to HTML with QuillJS styling
export const convertLocalVersionToHtml = (markdownContent) => {
  if (!markdownContent || typeof markdownContent !== 'string') return markdownContent;
  
  
  // First clean the markdown content
  let cleaned = cleanMarkdownContent(markdownContent);
  
  
  // Split content into lines for better processing
  let lines = cleaned.split('\n');
  let html = '';
  let inList = false;
  
  for (let i = 0; i < lines.length; i++) {
    let line = lines[i].trim();
    
    if (!line) {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      html += '<p><br></p>';
      continue;
    }
    
    // Handle headers
    if (line.startsWith('### ')) {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      html += `<h3 class="ql-size-large">${line.substring(4)}</h3>`;
    } else if (line.startsWith('## ')) {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      html += `<h2 class="ql-size-large">${line.substring(3)}</h2>`;
    } else if (line.startsWith('# ')) {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      html += `<h1 class="ql-size-large">${line.substring(2)}</h1>`;
    }
    // Handle bullet points
    else if (line.startsWith('- ')) {
      if (!inList) {
        html += '<ul class="ql-list ql-bullet">';
        inList = true;
      }
      html += `<li>${line.substring(2)}</li>`;
    }
    // Handle regular paragraphs
    else {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      html += `<p>${line}</p>`;
    }
  }
  
  // Close any remaining list
  if (inList) {
    html += '</ul>';
  }
  
  // Convert bold and italic text within the HTML
  html = html
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  
  return html;
};

// Function to process version content based on source
export const processVersionContent = (content, version) => {
  
  if (!content) return content;
  
  // Process hash tags to make them proper markdown headers
  let processedContent = content;
  
  // Remove percentage signs first
  processedContent = processedContent.replace(/%/g, '');
  
  // Handle the specific pattern: "TEXT## NUMBER. TEXT" (like "COURT PROCEEDING SUMMARY - U.S. SUPREME COURT## 1. CASE HEADER INFORMATION")
  processedContent = processedContent.replace(/(.*?)(#{2,})\s*(\d+\.\s*.*?)(?=\n|$)/g, (match, before, hashes, after) => {
    return before + '\n' + after;
  });

  // Handle hash tags at the beginning of lines like "## HEADER"
  processedContent = processedContent.replace(/^#{1,6}\s*(.*?)(?=\n|$)/gm, '\n$1\n');
  
  // Handle any remaining hash tags anywhere in the text (more aggressive)
  processedContent = processedContent.replace(/#{1,6}\s*([^#\n]+)/g, (match, headerText) => {
    // Extract just the header text without hash tags
    return '\n' + headerText.trim() + '\n';
  });
  
  // Final cleanup - remove any remaining hash tags
  processedContent = processedContent.replace(/#{1,6}/g, '');
  
  // Extra aggressive cleanup for the specific pattern we're seeing
  processedContent = processedContent.replace(/COURT PROCEEDING SUMMARY - U\.S\. SUPREME COURT## 1\. CASE HEADER INFORMATION/g, 'COURT PROCEEDING SUMMARY - U.S. SUPREME COURT\n1. CASE HEADER INFORMATION');
  
  // Clean up excessive spacing
  processedContent = processedContent.replace(/\n{3,}/g, '\n\n');
  
  return processedContent;
};